<template>
    <header v-editable="blok" data-test="NewL4HeaderContainer" :class="[isMobileMenuOpen && 'is-menu-open']">
        <!-- Begin: Header Top -->
        <div class="header-top">
            <div class="header-top__container md:container">
                <!-- Begin: Logo -->
                <div class="header-top__logo">
                    <template v-if="Object.values(paths.transfers)?.includes(route.params.type)">
                        <NuxtLink :to="`${domain}/`" external>
                            <LogoTransfers v-if="header.UseDefaultTransferLogo" />
                            <NuxtImg
                                v-else-if="header?.transfers_logo?.filename"
                                :src="header.transfers_logo.filename"
                                :title="header.transfers_logo.title"
                                :alt="header.transfers_logo.alt || 'logo'"
                                provider="storyblok"
                                format="webp"
                                loading="eager"
                            />
                        </NuxtLink>
                    </template>
                    <template v-else>
                        <NuxtLink :to="`${domain}/`" external>
                            <LogoMain v-if="header.UseDefaultLogo" />
                            <NuxtImg
                                v-else-if="header?.logo?.filename"
                                :src="header.logo.filename"
                                :title="header.logo.title"
                                :alt="header.logo.alt || 'logo'"
                                provider="storyblok"
                                format="webp"
                                loading="eager"
                                height="64"
                                width="474"
                            />
                        </NuxtLink>
                    </template>
                </div>
                <!-- End: Logo -->
                <!-- Begin: Mobile menu toggler -->
                <div class="header-top__toggler">
                    <button :class="['mobile-menu-toggler', isMobileMenuOpen && 'is-open']" @click="toggleMenu">
                        <span>Menu</span>
                    </button>
                </div>
                <!-- End: Mobile menu toggler -->

                <!-- Begin: Actions -->
                <div v-if="header" class="header-top__actions">
                    <NuxtLink
                        v-if="header.show_account"
                        :to="getL4InternalLink(header.account_link)"
                        data-gtm-id="header_top_manage_booking"
                        class="header-top__action"
                        external
                        aria-label="Manage Bookings"
                    >
                        <div class="header-tooltip" :data-label="header.account_label">
                            <NuxtIcon name="ri:calendar-todo-line" />
                        </div>
                    </NuxtLink>
                    <NuxtLink
                        v-if="header.show_help"
                        :to="getL4InternalLink(header.help_link)"
                        data-gtm-id="header_top_help"
                        class="header-top__action"
                        external
                        aria-label="Help & Support"
                    >
                        <div class="header-tooltip" :data-label="header.help_label">
                            <NuxtIcon name="ri:question-mark" :data-label="header.help_label" />
                        </div>
                    </NuxtLink>
                </div>
                <!-- End: Actions -->
            </div>
        </div>

        <!--         End: Header Top-->
        <!--         Begin: Header Navigation-->

        <div ref="headerMenu" class="header-menu" :style="menuTop">
            <div class="header-menu__container md:container">
                <nav class="header-menu__nav">
                    <ul class="header-menu__list">
                        <template v-for="(menu, index) in header.menu" :key="index">
                            <li>
                                <button
                                    v-if="menu.children.length"
                                    class="header-menu__submenu-btn"
                                    @click="setActiveSubmenu(index as number)"
                                >
                                    {{ menu.text }}
                                    <NuxtIcon name="ri:arrow-right-s-line" />
                                </button>
                                <div
                                    class="header-menu__item"
                                    :class="{
                                        'is-open': activeSubmenu === index,
                                        'header-menu__item--single': !menu.children.length,
                                    }"
                                >
                                    <button
                                        v-if="menu.children.length"
                                        class="header-menu__mobile-back-btn"
                                        aria-label="mobile-back-button"
                                        @click="setActiveSubmenu(-1)"
                                    >
                                        <NuxtIcon name="ri:arrow-left-long-line"></NuxtIcon>
                                    </button>
                                    <NuxtLink
                                        class="header-menu__link"
                                        :class="{ 'has-dropdown': menu.children.length }"
                                        :to="getL4InternalLink(menu.url)"
                                        :data-type="getDataType(menu)"
                                        :data-gtm-id="stringId('header_link', menu.trackingId, menu.text)"
                                        external
                                        @click="() => closeMobileMenu(menu.url)"
                                        >{{ menu.text }}
                                    </NuxtLink>
                                    <div
                                        v-if="menu.children.length"
                                        class="header-menu__dropdown"
                                        :class="{ 'two-columns': menu.children.length > 17 }"
                                    >
                                        <div class="header-menu__dropdown-pointer" />
                                        <div class="header-menu__dropdown-wrapper">
                                            <template v-for="(menuChild, menuIndex) in menu.children" :key="menuIndex">
                                                <NuxtLink
                                                    class="header-menu__sublink"
                                                    :to="getL4InternalLink(menuChild.url)"
                                                    :data-gtm-id="
                                                        stringId('header_sublink', menuChild.trackingId, menuChild.text)
                                                    "
                                                    :class="{ 'font-bold': menuChild.group_item }"
                                                    external
                                                    @click="() => closeMobileMenu(menuChild.url)"
                                                >
                                                    {{ menuChild.text }}
                                                </NuxtLink>
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </template>
                        <!-- Begin: Actions -->
                        <li v-if="header" class="header-menu__actions">
                            <NuxtLink
                                v-if="header.show_account"
                                :to="getL4InternalLink(header.account_link)"
                                data-gtm-id="header_top_manage_booking"
                                class="header-menu__action"
                                aria-label="Manage Bookings"
                                external
                            >
                                {{ header.account_label }}
                            </NuxtLink>
                            <NuxtLink
                                v-if="header.show_help"
                                :to="getL4InternalLink(header.help_link)"
                                data-gtm-id="header_top_help"
                                class="header-menu__action"
                                aria-label="Help & Support"
                                external
                            >
                                {{ header.help_label }}
                            </NuxtLink>
                        </li>
                        <!-- End: Actions -->
                    </ul>
                </nav>
            </div>
        </div>

        <!-- End: Header Navigation -->
    </header>
</template>

<script setup lang="ts">
import { useToggle } from '@vueuse/core';
import { stringId } from '~/utils/helpers';

const { blok } = defineProps<{ blok: IL4HeaderContainer }>();

const domain = useRuntimeConfig().public.SITE_URL;

const { content: header }: any = blok.header;

const headerMenu = ref(null);
const menuTop = ref({ '--menuTop': '0px' });

// Mobile menu toggle
const [isMobileMenuOpen, toggleMobileMenuOpen] = useToggle();

function toggleMenu() {
    toggleMobileMenuOpen();
    setActiveSubmenu(-1);
    const menuTopValue = headerMenu.value?.getBoundingClientRect()?.top || 0;
    menuTop.value = { '--menuTop': `${menuTopValue}px` };
}

useHead({
    bodyAttrs: {
        class: computed(() => {
            if (isMobileMenuOpen.value) return 'is-menu-open';
            return '';
        }),
    },
});
const getDataType = (link: any) => {
    // Function to check if the given string is included in either cached_url or url properties of the link object
    const matchUrl = (str: string) => link.url?.cached_url?.includes(str) || link.url?.url?.includes(str);

    // Filter through the URL types of the paths object to find matches in localized slugs
    const matchedTypes = Object.keys(paths).filter((urlType) => {
        // Get an array of all slug values for the current URL type using Object.values
        const localizedSlugs = Object.values(paths[urlType]).filter((slug: string) => slug.length);
        // Check if any of the slugs match the URL
        return localizedSlugs.some((slug: string) => matchUrl(slug));
    });

    // Default product type if no matches are found
    let productType = 'portparking';

    // Determine the product type based on the matched key, using a switch statement
    switch (true) {
        case matchedTypes.includes('transfers'):
            productType = 'transfers';
            break;
        case matchedTypes.includes('parking'):
            productType = 'airportparking';
            break;
        case matchedTypes.includes('hotel-with-parking'):
            productType = 'hotel';
            break;
        case matchedTypes.includes('train-station-parking'):
            productType = 'train';
            break;
    }

    // Return the determined product type
    return productType;
};

// Open or close (if index = -1) submenu on mobile
const activeSubmenu = ref<number>();

function setActiveSubmenu(index: number) {
    activeSubmenu.value = index;
}

// Close mobile menu if click on current page link
const route = useRoute();

function closeMobileMenu(linkObject: IL4Url) {
    const link: string = linkObject.url || linkObject.cached_url || '';
    if (link === route.path) {
        isMobileMenuOpen.value = false;
        setActiveSubmenu(-1);
    }
}

// Needs for creating urls for top rated car parks
import { useLinksStore } from '~/store/links';
import type { IL4HeaderContainer } from '~/types/components/L4HeaderContainer';
import type { IL4Url } from '~/types/components/L4Links';

const linksStore = useLinksStore();

watch(
    () => header,
    (value) => linksStore.updateLinks(value.menu),
    { immediate: true }
);
</script>

<style lang="postcss" scoped>
header {
    @apply sticky top-0 left-0 right-0 z-[100];
}

.header-reference {
    @apply h-[8rem];
}

/* Begin: Header Top */
.header-top {
    @apply py-3 px-3 bg-white static tablet:relative z-10;

    &__container {
        @apply flex justify-between items-center;
    }

    &__logo img,
    &__logo svg {
        @apply w-[250px] h-[34px] tablet:h-16 tablet:w-auto;
    }

    &__actions {
        @apply pl-8 hidden tablet:flex;
    }

    &__action {
        @apply px-2;

        &:not(:first-child) {
            @apply border-l border-l-primary -ml-[1px] pr-3;
        }

        &:last-child {
            /* Increased padding left to adjust tooltop position on near tablet and lg resolutions and prevent horizontal scroll */
            @apply pr-3;
        }

        .header-tooltip {
            @apply block relative;

            .iconify {
                @apply block w-[35px] h-5 bg-primary;
            }

            &:before,
            &:after {
                @apply absolute opacity-0 transition-opacity duration-200 top-full mt-1.5 left-1/2;
            }

            &:before {
                content: '';
                @apply w-8 h-8 bg-gray-300 rounded-full -translate-x-2/4 -translate-y-full;
            }

            &:after {
                content: attr(data-label);
                @apply text-white rounded bg-gray-500 min-w-[80px] px-1.5 py-1 mt-3 leading-4 left-1/2 -translate-x-2/4 text-sm text-center pointer-events-none;
            }

            &:hover {
                &::before,
                &:after {
                    @apply opacity-100;
                }
                .iconify {
                    @apply bg-gray-500;
                }
            }
        }
    }

    &__toggler {
        @apply tablet:hidden z-50 relative;
    }
}

/* End: Header Top */

/* Begin: Header Navigation */
.header-menu {
    height: calc(100vh - var(--menuTop));
    @apply absolute md:h-auto tablet:relative top-0 left-0 right-0 bg-primary w-full pt-16 tablet:py-3 overflow-hidden tablet:overflow-visible transition-transform duration-200 -translate-x-full tablet:translate-x-0;

    .is-menu-open & {
        @apply translate-x-0;
    }

    &__list {
        @apply flex flex-col tablet:flex-row px-4 tablet:px-0;
    }

    &__submenu-btn {
        @apply flex tablet:hidden relative py-2 text-white uppercase w-full text-xl;

        .iconify {
            @apply overflow-hidden absolute top-1/2 right-0 -translate-y-2/4 text-3xl;
        }
    }

    &__item {
        @apply text-white fixed tablet:relative pl-16 pt-5 tablet:pl-0 tablet:pt-0 tablet:mr-10 hidden tablet:flex flex-col tablet:flex-row inset-0 bg-[#734486] tablet:bg-transparent translate-x-full tablet:translate-x-0 transition-transform duration-200;

        &--single {
            @apply flex translate-x-0 relative pl-0 uppercase tablet:normal-case bg-transparent py-2 tablet:py-0;
        }

        &.is-open {
            @apply flex translate-x-0 z-30;
        }

        &:hover {
            .header-menu__link {
                @apply underline;
            }

            .header-menu__dropdown {
                @apply flex;

                &.two-columns {
                    @apply min-w-[478px];
                }
            }

            .header-menu__dropdown-wrapper {
                @apply flex tablet:flex-wrap flex-col tablet:max-h-[480px];
            }
        }
    }

    &__link {
        @apply text-xl tablet:text-base uppercase tablet:normal-case whitespace-pre-wrap;

        &.has-dropdown {
            @apply pl-6 relative;

            &::before {
                content: '';
                @apply hidden tablet:block absolute left-0 top-[0.3rem] h-2 w-2 border-white border-r-2 border-b-2 box-border rotate-45;
            }
        }
    }

    &__sublink {
        @apply px-2 py-1 tablet:bg-[#f7f2f9] text-base tablet:text-sm min-w-[230px] uppercase tablet:normal-case whitespace-pre-wrap;

        &:nth-of-type(2n + 1) {
            @apply tablet:bg-white;
        }

        &:hover {
            @apply bg-primary text-white;
        }
    }

    &__mobile-back-btn {
        @apply absolute tablet:hidden bg-primary left-0 top-0 h-full w-[60px];

        .iconify {
            @apply w-[35px] h-[25px] overflow-hidden absolute top-5 left-4;
            background-size: 514px 739px;
            background-position-x: -98px;
            background-position-y: -343px;
        }
    }

    &__dropdown {
        @apply relative tablet:absolute tablet:hidden top-4 tablet:top-8 p-2 flex flex-col tablet:bg-white tablet:border tablet:border-primary tablet:text-primary tablet:ring-4 tablet:ring-[#e9daef] tablet:ring-inset tablet:shadow whitespace-nowrap;

        &:before {
            content: '';
            @apply absolute left-0 right-0 -top-3 h-4;
        }
    }

    &__dropdown-pointer {
        @apply relative -top-1 hidden tablet:block;

        &:before,
        &:after {
            content: ' ';
            @apply absolute left-8 bottom-full w-0 h-0 pointer-events-none;
        }

        &:before {
            border-color: rgba(233, 218, 239, 0) rgba(233, 218, 239, 0) #e9daef;
            @apply ml-[16px] border-[16px];
        }

        &:after {
            @apply ml-[22px] border-[10px] border-transparent border-b-white;
        }
    }

    &__dropdown-wrapper {
        max-height: calc(100vh - 130px - var(--menuTop));
        @apply flex flex-col overflow-y-auto overflow-x-hidden sm:max-h-[calc(100vh-130px)];
    }

    &__actions {
        @apply tablet:hidden -mx-4 pt-3 mt-3 px-4 border-t-2 border-white;
    }

    &__action {
        @apply py-2 text-white uppercase leading-6 block text-xl;
    }
}

/* End: Header Navigation */

.mobile-menu-toggler {
    @apply block relative w-10 h-10 ml-2.5 bg-transparent z-50 border-none;
    text-indent: -9999px;

    &:before,
    &:after,
    span {
        @apply bg-primary rounded absolute left-1/4 right-1/4 h-1 -mt-0.5 duration-200 transition-all ease-linear;
    }

    &:before,
    &:after {
        content: '';
    }

    &:before {
        @apply top-[30%];
    }

    &:after {
        @apply top-[70%];
    }

    span {
        top: 50%;

        .is-open & {
            @apply opacity-0;
        }
    }

    &.is-open {
        &:before,
        &:after {
            @apply bg-white top-1/2 left-[15%] right-[15%];
        }

        &:before {
            @apply rotate-45;
        }

        &:after {
            @apply -rotate-45;
        }

        span {
            @apply opacity-0;
        }
    }
}
</style>

<style lang="postcss">
body.is-menu-open {
    @apply overflow-hidden h-screen sm:overflow-auto sm:h-auto;
}
</style>
